import React from 'react';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import Menu from 'antd/lib/menu';
import Tooltip from 'antd/lib/tooltip';
import { MoreOutlined, RightOutlined } from '@ant-design/icons';
import { t } from 'utils/i18n';
import type { Detection, ModelView } from './reference-model';
import LibraryThumb from './library-thumb';

interface Props {
    detection: Detection;
    model: ModelView | null;
    crop: string | undefined;
    selected: boolean;
    // the .blend on the Windows share; undefined while asked for, null when not on the share
    windowsPath?: string | null;
    // shown when the list spans several photos
    photoLabel?: string;
    onSelect(): void;
    onPick(): void;
    onCopyPath?(path: string): void;
    onDelete?(): void;
}

export default function DetectionRow(props: Props): JSX.Element {
    const {
        detection, model, crop, selected, windowsPath, photoLabel, onSelect, onPick, onCopyPath, onDelete,
    } = props;
    const classes = ['cvat-references-row'];
    if (selected) classes.push('cvat-references-row-selected');
    if (detection.hidden) classes.push('cvat-references-row-hidden');

    // the destructive action sits behind the menu, as in the Objects list; a click in the
    // overlay bubbles through the React tree to the row, so it must not select it
    const menu = (
        <Menu onClick={({ domEvent }) => domEvent.stopPropagation()}>
            {model && onCopyPath && (
                <Menu.Item key='copy' disabled={!windowsPath} onClick={() => windowsPath && onCopyPath(windowsPath)}>
                    {windowsPath === null ? t('Not on the library share') : t('Copy path')}
                </Menu.Item>
            )}
            {onDelete && <Menu.Item key='delete' danger onClick={onDelete}>{t('Delete')}</Menu.Item>}
        </Menu>
    );

    return (
        <div
            role='button'
            tabIndex={0}
            className={classes.join(' ')}
            onClick={onSelect}
            onKeyDown={(event) => {
                if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    onSelect();
                }
            }}
        >
            {crop ? (
                <img className='cvat-references-crop' src={crop} alt='' />
            ) : (
                <div className='cvat-references-crop cvat-references-crop-placeholder' />
            )}
            {model && (
                <>
                    <RightOutlined className='cvat-references-model-arrow' />
                    <LibraryThumb
                        className='cvat-references-model-thumb'
                        src={model.imageUrl}
                        fullSrc={model.fullImageUrl}
                    />
                </>
            )}
            <div className='cvat-references-row-body'>
                {photoLabel && <span className='cvat-references-photo-label'>{photoLabel}</span>}
                {model ? (
                    // a long file name is cut to two lines, the full one is in the tooltip
                    <Tooltip title={model.name}>
                        <span className='cvat-references-model-name'>{model.name}</span>
                    </Tooltip>
                ) : (
                    <span className='cvat-references-no-model'>{t('No model yet')}</span>
                )}
                <Button
                    type='link'
                    size='small'
                    className='cvat-references-pick'
                    onClick={(event) => {
                        event.stopPropagation();
                        onPick();
                    }}
                >
                    {model ? t('Change') : t('Pick')}
                </Button>
            </div>
            {onDelete && (
                <Dropdown overlay={menu} trigger={['click']} placement='bottomRight' destroyPopupOnHide>
                    <Button
                        type='text'
                        size='small'
                        className='cvat-references-more'
                        aria-label={t('More')}
                        icon={<MoreOutlined />}
                        onClick={(event) => event.stopPropagation()}
                    />
                </Dropdown>
            )}
        </div>
    );
}
